import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { AvatarInput, TextInput, PhoneInput } from "../../components/inputs";
import { EmailChangeVerifyDialog } from "../../components/settings/emailChangeVerifyDialog";
import { errorToast, isValidEmail, successToast } from "../../utils";
import { requestUserEmailChange, updateUser } from "../../store/auth/authActions";
import { Toaster } from "react-hot-toast";

const normalizeEmail = (value) => (value || "").toLowerCase().trim();

const profileUpdatePayload = (data) => ({
    firstName: data.firstName,
    lastName: data.lastName,
    country: data.country,
    phone: data.phone,
    avatarId: data.avatarId,
    password: data.password,
});

const UserSettings = () => {

    const { user } = useSelector((state) => state.auth);

    const dispatch = useDispatch()
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [pendingProfileData, setPendingProfileData] = useState(null);
    const [verifyTargetEmail, setVerifyTargetEmail] = useState("");
    const {
        register,
        handleSubmit,
        control,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            firstName: user.firstName,
            lastName: user.lastName,
            userId: user._id,
            country: user.country,
            avatar: user.avatar,
            phone: user.phone || null,
            email: user.email
        }
    });

    const saveProfileFields = useCallback(async (data) => {
        await dispatch(updateUser(profileUpdatePayload(data))).unwrap();
        successToast("Informations changed successfully");
        setValue("avatarId", "");
        setValue("password", "");
    }, [dispatch, setValue]);

    const onSubmit = async (data) => {
        const emailChanged =
            normalizeEmail(data.email) !== normalizeEmail(user?.email);

        if (emailChanged) {
            setPendingProfileData(data);
            setVerifyTargetEmail(normalizeEmail(data.email));
            try {
                await dispatch(
                    requestUserEmailChange({ email: data.email }),
                ).unwrap();
                setEmailDialogOpen(true);
                successToast("We emailed a verification code to your new address");
            } catch (e) {
                errorToast(e?.error || "Could not send verification email");
                setPendingProfileData(null);
                setVerifyTargetEmail("");
            }
            return;
        }

        try {
            await saveProfileFields(data);
        } catch (e) {
            errorToast(e?.error || "Could not save your changes");
        }
    };

    const handleEmailDialogCancel = useCallback(() => {
        setEmailDialogOpen(false);
        setPendingProfileData(null);
        setVerifyTargetEmail("");
        setValue("email", user?.email || "");
    }, [setValue, user?.email]);

    const handleAfterEmailVerified = useCallback(async () => {
        if (!pendingProfileData) return;
        const payload = pendingProfileData;
        try {
            await dispatch(updateUser(profileUpdatePayload(payload))).unwrap();
            successToast("Informations changed successfully");
        } catch (e) {
            errorToast(
                e?.error ||
                "Your email was updated. Use Save again to apply other changes.",
            );
        } finally {
            setEmailDialogOpen(false);
            setPendingProfileData(null);
            setVerifyTargetEmail("");
            setValue("avatarId", "");
            setValue("password", "");
        }
    }, [dispatch, pendingProfileData, setValue]);

    return (
        <>
            <Toaster containerStyle={{zIndex: 9999999999 }} />
            <form noValidate id="user-form" className={"bg-white rounded-2xl p-5 w-full xl:w-2/4 mx-auto flex flex-col"} onSubmit={handleSubmit(onSubmit)}>
                <AvatarInput
                    name="avatar"
                    errors={errors}
                    register={register}
                    setValue={setValue}
                    defaultValue={user.avatar}
                    required
                />

                <div className="flex flex-col md:flex-row xl:flex-col 2xl:flex-row gap-2 md:gap-10 xl:gap-2 2xl:gap-10 mt-5">
                    <TextInput
                        name="firstName"
                        label="First Name"
                        errors={errors}
                        register={register}
                        control={control}
                        validationSchema={{
                            required: "This field is required"
                        }}
                    />
                    <TextInput
                        name="lastName"
                        label="Last Name"
                        errors={errors}
                        register={register}
                        control={control}
                        validationSchema={{
                            required: "This field is required"
                        }}
                    />
                </div>

                <TextInput
                    name="email"
                    label="Email"
                    errors={errors}
                    type="email"
                    register={register}
                    control={control}
                    validationSchema={{
                        required: "This field is required",
                        validate: (value) =>
                            isValidEmail(value) || "Please enter a valid email address",
                    }}
                />

                <PhoneInput
                    name="phone"
                    label="Phone"
                    className="phoneInput"
                    errors={errors}
                    register={register}
                    control={control}
                />

                
                <TextInput
                    name="password"
                    label="New Password"
                    className=""
                    type={"password"}
                    errors={errors}
                    register={register}
                    validationSchema={{
                        validate: (v) => (v.trim() != "" && v.length < 8) ? "The password must be at least 8 characters long": true
                    }}
                    control={control}
                />

                <button className="bg-nt-blue text-white p-2 px-4 rounded self-center">
                    Save
                </button>

            </form>
            <EmailChangeVerifyDialog
                open={emailDialogOpen}
                targetEmail={verifyTargetEmail}
                onCancel={handleEmailDialogCancel}
                onSuccess={handleAfterEmailVerified}
            />

        </>
    )
}
export { UserSettings }